// src/pages/admin/CourseLectures/lectures/Lecture10Unterkunft.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${
        isOpen ? "border-[#009fe3]/50" : "border-gray-200"
      } bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
          isOpen ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${
            isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${
            isOpen ? "text-[#0369a1]" : "text-gray-800"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Option Card ───────────────────────────────────────────────────
const OptionCard: React.FC<{
  icon: string;
  title: string;
  subtitle: string;
  price: string;
  forWhom: string;
  color: string;
}> = ({ icon, title, subtitle, price, forWhom, color }) => (
  <div className={`rounded-xl border p-3 ${color}`}>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-2xl">{icon}</span>
      <div>
        <p className="text-sm font-bold">{title}</p>
        <p className="text-[11px] opacity-70">{subtitle}</p>
      </div>
    </div>
    <div className="space-y-1">
      <p className="text-xs font-semibold opacity-80">💶 {price}</p>
      <p className="text-xs opacity-70">👤 {forWhom}</p>
    </div>
  </div>
);

// ── Term Card ─────────────────────────────────────────────────────
const TermCard: React.FC<{
  term: string;
  translation: string;
  desc: string;
  color: string;
  badge?: string;
  badgeColor?: string;
}> = ({ term, translation, desc, color, badge, badgeColor }) => (
  <div className={`rounded-xl border p-3 ${color}`}>
    <div className="flex items-start justify-between gap-2 mb-1.5">
      <div>
        <p className="text-sm font-bold">{term}</p>
        <p className="text-[11px] opacity-70 italic">{translation}</p>
      </div>
      {badge && (
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${badgeColor}`}
        >
          {badge}
        </span>
      )}
    </div>
    <p className="text-xs opacity-90">{desc}</p>
  </div>
);

// ── Website Card ──────────────────────────────────────────────────
const WebsiteCard: React.FC<{
  num: string;
  name: string;
  url: string;
  desc: string;
  color: string;
  badge?: string;
  badgeColor?: string;
}> = ({ num, name, url, desc, color, badge, badgeColor }) => (
  <div className={`rounded-xl border p-3 ${color}`}>
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-xs font-extrabold flex-shrink-0">
        {num}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-sm font-bold">{name}</p>
          {badge && (
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badgeColor}`}
            >
              {badge}
            </span>
          )}
        </div>
        <a
          href={`https://www.${url}`}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-[#009fe3] underline font-semibold block mb-1"
        >
          {url}
        </a>
        <p className="text-xs opacity-80">{desc}</p>
      </div>
    </div>
  </div>
);

// ── Scam Warning Row ──────────────────────────────────────────────
const ScamRow: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2">
    <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">⚠️</span>
    <p className="text-xs text-red-700">{text}</p>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture10Unterkunft: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 09 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Fokus auf Qualität — Anschreiben mit PAR-Methode",
            "Update an G2G — Eingangsbestätigungen & Absagen gemeldet",
            "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Unterkunfts-Optionen — WG, Apartment, Hostel" },
          { num: "2", label: "WG (Wohngemeinschaft) — Shared Flat" },
          { num: "3", label: "Private Wohnung (Apartment)" },
          { num: "4", label: "Hostel / Airbnb — nur kurzfristig" },
          { num: "5", label: "Wichtige Begriffe — Anmeldung, Miete, Kaution" },
          { num: "6", label: "Scam-Warnung — Betrug erkennen & vermeiden" },
          { num: "7", label: "Top Websites — Studenten & Budget" },
          { num: "8", label: "Top Websites — Langfristig & International" },
          { num: "9", label: "Tipps & Strategie" },
        ]}
      />

      {/* 01 — Übersicht */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="🏠"
        title="Unterkunfts-Optionen — Überblick"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Nach der Visa-Genehmigung ist die Unterkunft die größte
          Herausforderung. In Deutschland kann es schwieriger sein, eine Wohnung
          zu finden, als ein Vorstellungsgespräch zu bestehen. Diese drei
          Optionen stehen zur Auswahl:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <OptionCard
            icon="🤝"
            title="WG"
            subtitle="Wohngemeinschaft — Shared Flat"
            price="300 – 600 € / Monat"
            forWhom="Studierende & Azubis — empfohlen"
            color="bg-blue-50 border-blue-200 text-blue-800"
          />
          <OptionCard
            icon="🏢"
            title="Private Wohnung"
            subtitle="Apartment — komplett für dich"
            price="700 – 1.500+ € / Monat"
            forWhom="Familien & hochverdienende Fachkräfte"
            color="bg-purple-50 border-purple-200 text-purple-800"
          />
          <OptionCard
            icon="🛏️"
            title="Hostel / Airbnb"
            subtitle="Temporäre Unterkunft"
            price="Variiert — teurer langfristig"
            forWhom="Nur für die ersten 1–2 Wochen"
            color="bg-orange-50 border-orange-200 text-orange-800"
          />
        </div>
        <InfoBox variant="warning" icon="⚠️" title="Wichtiger Hinweis">
          In den meisten Hostels ist keine <strong>Anmeldung</strong>{" "}
          (Stadtregistrierung) möglich. Ohne Anmeldung kannst du kein Bankkonto
          eröffnen und dein Visum nicht verlängern.
        </InfoBox>
      </Accordion>

      {/* 02 — WG */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="🤝"
        title="WG (Wohngemeinschaft) — Shared Flat"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 mb-4">
          <p className="text-sm font-bold text-blue-800 mb-1">
            Was ist eine WG?
          </p>
          <p className="text-xs text-blue-700">
            In einer großen Wohnung mietet jede Person ein einzelnes Zimmer.
            Küche und Bad werden gemeinsam genutzt. Die beliebteste Option für
            Studierende und Azubis in Deutschland.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-800 mb-2">✅ Vorteile</p>
            <div className="space-y-1.5">
              {[
                "Günstig — 300–600 € je nach Stadt",
                "Möbel sind meistens vorhanden (furnished)",
                "Neue Menschen kennenlernen",
                "Anmeldung ist möglich",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-green-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-bold text-amber-800 mb-2">
              💡 Tipps für mehr Erfolg
            </p>
            <div className="space-y-1.5">
              {[
                "Profil sorgfältig gestalten — Foto und kurze Vorstellung",
                "Nachrichten auf Deutsch schreiben — Antwortchance steigt deutlich",
                "Schnell bewerben — gute Zimmer sind schnell weg",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-amber-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] px-4 py-3">
          <p className="text-xs font-bold text-[#0369a1] mb-1">
            👥 Für wen ist die WG am besten geeignet?
          </p>
          <p className="text-xs text-[#0369a1]">
            Für alle, die eine Ausbildung beginnen oder studieren. Die WG ist
            die <strong>günstigste und unkomplizierteste</strong> Option am
            Anfang.
          </p>
        </div>
      </Accordion>

      {/* 03 — Private Wohnung */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="🏢"
        title="Private Wohnung (Apartment) — Alles für dich allein"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 mb-4">
          <p className="text-sm font-bold text-purple-800 mb-1">
            Was ist eine private Wohnung?
          </p>
          <p className="text-xs text-purple-700">
            Die gesamte Wohnung gehört nur dir — vollständige Privatsphäre.
            Allerdings ist dies die teuerste und aufwändigste Option.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-800 mb-2">✅ Vorteile</p>
            <div className="space-y-1.5">
              {[
                "Vollständige Privatsphäre",
                "Ideal für Familien",
                "Kein Rücksicht auf Mitbewohner nötig",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-green-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-bold text-red-800 mb-2">❌ Nachteile</p>
            <div className="space-y-1.5">
              {[
                "700–1.500+ € pro Monat — sehr teuer",
                "Unfurnished — keine Möbel, keine Küche, kein Licht",
                "Ohne Schufa (Kredithistorie) kaum möglich",
                "Sehr hohe Konkurrenz auf dem Wohnungsmarkt",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-red-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <InfoBox variant="info" icon="👨‍👩‍👧" title="Empfehlung">
          Für Azubis am Anfang: Starte mit einer WG. Eine private Wohnung lohnt
          sich erst, wenn du ein geregeltes Einkommen und eine Schufa-Geschichte
          hast.
        </InfoBox>
      </Accordion>

      {/* 04 — Hostel */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="🛏️"
        title="Hostel / Airbnb — Nur für die ersten 1–2 Wochen"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 mb-4">
          <p className="text-sm font-bold text-orange-800 mb-1">
            Hostels und Airbnb — temporäre Lösung
          </p>
          <p className="text-xs text-orange-700">
            Diese Option eignet sich ausschließlich für die erste Ankunft in
            Deutschland, während du aktiv nach einer dauerhaften Unterkunft
            suchst. Plane maximal 1–2 Wochen ein.
          </p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-bold text-red-800 mb-3">
            🚨 Drei kritische Probleme mit dem Hostel
          </p>
          <div className="space-y-2">
            {[
              {
                title: "Keine Anmeldung möglich",
                desc: "In den meisten Hostels kannst du keine Stadtregistrierung (Anmeldung) vornehmen.",
              },
              {
                title: "Kein Bankkonto ohne Anmeldung",
                desc: "Ein deutsches Bankkonto lässt sich ohne Anmeldungsbestätigung nicht eröffnen.",
              },
              {
                title: "Keine Visa-Verlängerung ohne Anmeldung",
                desc: "Die Verlängerung des Aufenthaltstitels erfordert eine gültige Anmeldung.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2"
              >
                <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">
                  !
                </span>
                <div>
                  <p className="text-xs font-bold text-red-800">{item.title}</p>
                  <p className="text-xs text-red-700 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <InfoBox variant="tip" icon="💡" title="Strategie">
          Buche das Hostel nur für die Ankunftswoche. Suche parallel sofort nach
          einer WG — mindestens 1–2 Monate vor der Abreise beginnen.
        </InfoBox>
      </Accordion>

      {/* 05 — Wichtige Begriffe */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="📖"
        title="Wichtige Begriffe — Anmeldung, Miete & Kaution"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Diese drei deutschen Begriffe musst du unbedingt kennen, bevor du eine
          Wohnung oder ein WG-Zimmer mietest:
        </p>
        <div className="space-y-3">
          <TermCard
            term="Anmeldung"
            translation="Stadtregistrierung"
            desc="Innerhalb von 14 Tagen nach Ankunft musst du dich beim Stadtamt anmelden. Der Vermieter muss dir dafür eine Wohnungsgeberbestätigung ausstellen. Frage immer vorab: 'Is Anmeldung possible?' Ohne Anmeldung: kein Konto, keine Visa-Verlängerung."
            color="bg-blue-50 border-blue-200 text-blue-800"
            badge="Pflicht in 14 Tagen"
            badgeColor="bg-red-100 text-red-700 border border-red-200"
          />

          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="text-sm font-bold text-green-800 mb-2">
              Warmmiete vs Kaltmiete
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="bg-white/70 rounded-lg border border-green-200 px-3 py-2">
                <p className="text-xs font-bold text-green-800">
                  Kaltmiete (Cold Rent)
                </p>
                <p className="text-xs text-green-700 mt-0.5">
                  Nur die Miete für das Zimmer / die Wohnung — ohne Nebenkosten.
                </p>
              </div>
              <div className="bg-white/70 rounded-lg border border-green-200 px-3 py-2">
                <p className="text-xs font-bold text-green-800">
                  Warmmiete (Warm Rent)
                </p>
                <p className="text-xs text-green-700 mt-0.5">
                  Miete + Heizung + Wasser + Müllgebühren. Strom und Internet
                  oft separat.
                </p>
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2 opacity-80">
              Tipp: Immer nach Warmmiete fragen — so kennst du die echten
              monatlichen Kosten.
            </p>
          </div>

          <TermCard
            term="Kaution"
            translation="Sicherheitsleistung / Deposit"
            desc="Vor dem Einzug zahlst du in der Regel 2–3 Monatsmieten als Kaution. Das Geld bekommst du zurück, wenn du auszieht und die Wohnung in gutem Zustand hinterlässt. Zahle niemals Kaution ohne einen offiziellen Mietvertrag."
            color="bg-amber-50 border-amber-200 text-amber-800"
            badge="2–3 Monatsmieten"
            badgeColor="bg-amber-100 text-amber-800"
          />
        </div>
      </Accordion>

      {/* 06 — Scam */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="🚨"
        title="Scam-Warnung — Betrug erkennen & vermeiden"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 mb-4">
          <p className="text-sm font-bold text-red-800 mb-1">
            ⚠️ Ausländische Studierende sind häufig Ziel von Betrug!
          </p>
          <p className="text-xs text-red-700">
            Erkenne diese typischen Warnsignale sofort und reagiere nicht
            darauf.
          </p>
        </div>
        <div className="space-y-2 mb-4">
          <ScamRow text="Der Vermieter gibt an, sich gerade im Ausland zu befinden (z.B. in England) und die Besichtigung nicht möglich sei." />
          <ScamRow text="Der Schlüssel soll per Kurier geliefert werden — aber zuerst soll Geld via Western Union oder MoneyGram überwiesen werden." />
          <ScamRow text="Geld wird verlangt, bevor du die Wohnung gesehen hast oder einen Mietvertrag unterzeichnet hast." />
          <ScamRow text="Der Mietpreis ist unrealistisch günstig — deutlich unter dem Marktpreis für die Stadt." />
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-bold text-green-800 mb-2">
            ✅ Schutzmassnahmen
          </p>
          <div className="space-y-1.5">
            {[
              "Niemals Geld überweisen, bevor du einen offiziellen Mietvertrag hast",
              "Niemals Geld überweisen, bevor du den Schlüssel in der Hand hältst",
              "Nutze sichere Plattformen wie HousingAnywhere oder Wunderflats für die Buchung aus dem Ausland",
              "Bei Verdacht: Anzeige ignorieren und zur nächsten weitergehen",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
              >
                <span className="text-green-500 font-bold flex-shrink-0 mt-0.5">
                  ✓
                </span>
                <p className="text-xs text-green-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 07 — Top Websites: Budget */}
      <Accordion
        id="07"
        num="ABSCHNITT 07"
        icon="🌐"
        title="Top Websites — Studenten & Budget (Nr. 1–5)"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Diese Plattformen sind am günstigsten und am besten für Azubis und
          Studierende geeignet:
        </p>
        <div className="space-y-2 mb-4">
          <WebsiteCard
            num="1"
            name="WG-Gesucht.de"
            url="wg-gesucht.de"
            desc="Die Nummer-1-Website für WG-Zimmer in Deutschland. Profil sorgfältig ausfüllen und Nachrichten auf Deutsch schreiben — das erhöht die Antwortchance erheblich."
            color="bg-blue-50 border-blue-200 text-blue-800"
            badge="Nr. 1 empfohlen"
            badgeColor="bg-[#009fe3] text-white"
          />
          <WebsiteCard
            num="2"
            name="Studierendenwerk"
            url="studierendenwerk.de"
            desc="Jede Universitätsstadt hat eigene Studentenwohnheime. Am günstigsten: 250–400 €. Direkt nach dem Zulassungsbescheid bewerben — die Wartelisten sind sehr lang."
            color="bg-green-50 border-green-200 text-green-800"
            badge="Günstigste Option"
            badgeColor="bg-green-700 text-white"
          />
          <WebsiteCard
            num="3"
            name="ImmobilienScout24.de"
            url="immobilienscout24.de"
            desc="Deutschlands größtes Immobilienportal. Sehr viele Angebote — aber auch sehr hohe Konkurrenz. Eher für langfristige Wohnungssuche geeignet."
            color="bg-purple-50 border-purple-200 text-purple-800"
            badge="Größtes Portal"
            badgeColor="bg-purple-700 text-white"
          />
          <WebsiteCard
            num="4"
            name="Immowelt.de"
            url="immowelt.de"
            desc="Ebenfalls sehr beliebt und zuverlässig. Gute Alternative zu ImmobilienScout24 für private Wohnungen."
            color="bg-indigo-50 border-indigo-200 text-indigo-800"
          />
          <WebsiteCard
            num="5"
            name="Kleinanzeigen.de"
            url="kleinanzeigen.de"
            desc="Deutschlands Kleinanzeigen-Plattform (wie ein lokales Craigslist). Oft direkt mit dem Vermieter verhandeln — günstigere Preise möglich. Betrug-Risiko beachten."
            color="bg-amber-50 border-amber-200 text-amber-800"
            badge="Direkt vom Vermieter"
            badgeColor="bg-amber-600 text-white"
          />
        </div>
      </Accordion>

      {/* 08 — Top Websites: International */}
      <Accordion
        id="08"
        num="ABSCHNITT 08"
        icon="✈️"
        title="Top Websites — International & Sicher buchen (Nr. 6–10)"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Diese Plattformen sind etwas teurer, aber sicher aus dem Ausland
          buchbar — kein Betrugsrisiko:
        </p>
        <div className="space-y-2 mb-4">
          <WebsiteCard
            num="6"
            name="HousingAnywhere"
            url="housinganywhere.com"
            desc="Speziell für internationale Studierende entwickelt. Sicheres Zahlungssystem — Geld wird erst nach Einzug freigegeben. Ideal für Buchung aus Bangladesch."
            color="bg-teal-50 border-teal-200 text-teal-800"
            badge="Sicher aus dem Ausland"
            badgeColor="bg-teal-700 text-white"
          />
          <WebsiteCard
            num="7"
            name="Wunderflats"
            url="wunderflats.com"
            desc="Möblierte Apartments ohne Aufwand. Schnell verfügbar, kein langer Bewerbungsprozess — aber etwas teurer als WG-Zimmer."
            color="bg-sky-50 border-sky-200 text-sky-800"
            badge="Möbliert & schnell"
            badgeColor="bg-sky-700 text-white"
          />
          <WebsiteCard
            num="8"
            name="Spotahome"
            url="spotahome.com"
            desc="Virtuelle Video-Touren der Wohnungen — du kannst die Unterkunft ohne Besichtigung vor Ort buchen. Sehr praktisch für die Suche aus dem Ausland."
            color="bg-rose-50 border-rose-200 text-rose-800"
            badge="Video-Tour verfügbar"
            badgeColor="bg-rose-700 text-white"
          />
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-xs font-extrabold flex-shrink-0">
                9
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-sm font-bold text-blue-800">
                    Facebook-Gruppen
                  </p>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-700 text-white">
                    Community
                  </span>
                </div>
                <p className="text-xs text-blue-700 mt-1">
                  Suche auf Facebook nach:{" "}
                  <strong>"Accommodation in [Stadtname]"</strong> oder{" "}
                  <strong>"Bangladeshis in Germany"</strong>. Oft posten
                  Landsleute, wenn sie aus ihrer WG ausziehen — schnell
                  reagieren!
                </p>
              </div>
            </div>
          </div>
          <WebsiteCard
            num="10"
            name="HC24 (HomeCompany)"
            url="homecompany.de"
            desc="Gute Option für kurzfristige möblierte Unterkünfte. Ideal für die Übergangsphase nach der Ankunft."
            color="bg-gray-50 border-gray-200 text-gray-800"
          />
        </div>
      </Accordion>

      {/* 09 — Tipps */}
      <Accordion
        id="09"
        num="ABSCHNITT 09"
        icon="💡"
        title="Tipps & Strategie — So findest du schneller eine Unterkunft"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Die Wohnungssuche in Deutschland ist ein Geduldspiel. Im Durchschnitt
          erhält man nach 20–30 Nachrichten nur eine Antwort. Lass dich nicht
          entmutigen.
        </p>
        <div className="space-y-3">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-bold text-blue-800 mb-2">
              ⏰ Frühzeitig beginnen
            </p>
            <p className="text-xs text-blue-700">
              Starte die Wohnungssuche mindestens{" "}
              <strong>1–2 Monate vor dem Flug</strong> oder dem
              Ausbildungsbeginn. Gute Zimmer sind innerhalb von Stunden weg.
            </p>
          </div>

          <div className="rounded-xl border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-bold text-green-800 mb-2">
              🗣️ Auf Deutsch schreiben
            </p>
            <p className="text-xs text-green-700 mb-2">
              Nachrichten an Vermieter auf Deutsch steigern die Antwortchance um
              bis zu <strong>80%</strong>. Nutze Google Translate oder ChatGPT
              für die Übersetzung.
            </p>
            <div className="bg-white/70 rounded-lg border border-green-200 px-3 py-2">
              <p className="text-xs font-bold text-green-700 mb-1">
                Muster-Nachricht:
              </p>
              <p className="text-xs text-green-700 italic">
                "Guten Tag, mein Name ist [Name]. Ich beginne im [Monat] meine
                Ausbildung als [Beruf] in [Stadt] und suche ein Zimmer ab
                [Datum]. Ist eine Anmeldung möglich? Mit freundlichen Grüßen,
                [Name]"
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
            <p className="text-sm font-bold text-purple-800 mb-2">
              ✅ Checkliste vor der Zusage
            </p>
            <div className="space-y-1.5">
              {[
                "Ist Anmeldung (Wohnungsgeberbestätigung) möglich?",
                "Warmmiete oder Kaltmiete — was ist alles inklusive?",
                "Wie hoch ist die Kaution und wann wird sie fällig?",
                "Gibt es einen offiziellen Mietvertrag?",
                "Wie lange ist die Mindestmietdauer?",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="w-3.5 h-3.5 rounded border border-purple-400 flex-shrink-0 bg-white" />
                  <p className="text-xs text-purple-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-bold text-amber-800 mb-2">
              📊 Realistische Erwartungen
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
              {[
                { num: "20–30", label: "Nachrichten gesendet" },
                { num: "1–3", label: "Antworten erhalten" },
                { num: "1", label: "Zimmer gefunden" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white/70 rounded-xl border border-amber-200 px-3 py-2"
                >
                  <p className="text-lg font-extrabold text-amber-700">
                    {item.num}
                  </p>
                  <p className="text-[10px] text-amber-600">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-700 mt-2 text-center opacity-80">
              Das ist normal — bleib dran!
            </p>
          </div>
        </div>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Mit der richtigen Strategie findest du deine Unterkunft in
              Deutschland.
            </p>
            <ul className="space-y-1">
              {[
                "Azubis & Studierende: WG (WG-Gesucht.de) ist die beste Wahl",
                "Anmeldung immer vorab klären — sie ist für alles notwendig",
                "Warmmiete vs Kaltmiete verstehen — echte Kosten kennen",
                "Kaution nur mit offiziellem Mietvertrag zahlen",
                "Scam-Warnsignale kennen und nicht darauf eingehen",
                "1–2 Monate vor Abreise mit der Suche beginnen",
                "Nachrichten auf Deutsch schreiben — 80% mehr Antworten",
                "Aus dem Ausland sicher buchen: HousingAnywhere, Spotahome",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="📋"
        title="Deine Aufgaben — Lektion 10"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>WG-Gesucht.de Profil erstellen</strong> — Foto,
                Vorstellung und Wunschstadt eintragen
              </>,
              <>
                <strong>Deutsche Muster-Nachricht vorbereiten</strong> — mit
                Ausbildungsberuf, Startdatum und Anmeldung-Frage
              </>,
              "Mindestens 10 WG-Inserate kontaktieren — auf Deutsch",
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture10Unterkunft;
